import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import {
  SavedTableView,
  TABLE_VIEWS_SETTING_KEY,
  TableColumnConfig,
  TableViewsSettingValue,
} from 'models';
import { selectProfile } from 'store/profile';
import {
  useCreateUserSettings,
  useDeleteUserSettings,
  useEditUserSettings,
  useUserSettingsList,
} from 'features/userSettings';

const emptyValue: TableViewsSettingValue = { views: [], activeViewId: null };

export const normalizeTableViewsSettingValue = (value: unknown): TableViewsSettingValue => {
  if (!value || typeof value !== 'object') {
    return emptyValue;
  }

  const typed = value as TableViewsSettingValue;
  const views = Array.isArray(typed.views) ? typed.views : [];

  return {
    views,
    activeViewId: views.length ? typed.activeViewId ?? null : null,
  };
};

export const useTableViewsSetting = (scope: string) => {
  const { data: userSettings, isLoading } = useUserSettingsList();
  const userProfile = useSelector(selectProfile);
  const { mutateAsync: createUserSettings, isPending: isCreating } = useCreateUserSettings();
  const { mutateAsync: editUserSettings, isPending: isEditing } = useEditUserSettings();
  const { mutateAsync: deleteUserSettings, isPending: isDeleting } = useDeleteUserSettings();

  const setting = useMemo(
    () =>
      userSettings?.find((item) => item.scope === scope && item.key === TABLE_VIEWS_SETTING_KEY),
    [userSettings, scope]
  );

  const value = useMemo(() => normalizeTableViewsSettingValue(setting?.value), [setting?.value]);

  const persistValue = useCallback(
    async (nextValue: TableViewsSettingValue) => {
      if (!nextValue.views.length) {
        if (setting?.id) {
          await deleteUserSettings({ id: setting.id, key: TABLE_VIEWS_SETTING_KEY });
        }
        return;
      }

      if (setting?.id) {
        await editUserSettings({
          id: setting.id,
          data: {
            scope,
            key: TABLE_VIEWS_SETTING_KEY,
            value: nextValue,
          },
        });
        return;
      }

      await createUserSettings({
        userId: userProfile?.id,
        scope,
        key: TABLE_VIEWS_SETTING_KEY,
        value: nextValue,
      });
    },
    [setting?.id, deleteUserSettings, editUserSettings, createUserSettings, scope, userProfile?.id]
  );

  const saveView = useCallback(
    async (name: string, columns: TableColumnConfig[]) => {
      const view: SavedTableView = {
        id: uuid(),
        name,
        columns,
      };

      const nextValue: TableViewsSettingValue = {
        views: [...value.views, view],
        activeViewId: view.id,
      };

      await persistValue(nextValue);
      return view;
    },
    [persistValue, value.views]
  );

  const selectView = useCallback(
    async (viewId: string) => {
      const nextValue: TableViewsSettingValue = {
        ...value,
        activeViewId: viewId,
      };
      await persistValue(nextValue);
      return value.views.find((view) => view.id === viewId) ?? null;
    },
    [persistValue, value]
  );

  const deleteView = useCallback(
    async (viewId: string) => {
      const views = value.views.filter((view) => view.id !== viewId);
      const activeViewId =
        value.activeViewId === viewId ? views[0]?.id ?? null : value.activeViewId ?? null;

      await persistValue({ views, activeViewId });
      return { views, activeViewId, deletedLast: views.length === 0 };
    },
    [persistValue, value]
  );

  return {
    views: value.views,
    activeViewId: value.activeViewId ?? null,
    isLoading,
    isSaving: isCreating || isEditing || isDeleting,
    saveView,
    selectView,
    deleteView,
  };
};
